
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInAnonymously,
    signOut,
    onAuthStateChanged,
    signInWithPopup,
    signInWithCredential,
    linkWithCredential,
    linkWithPopup,
    sendPasswordResetEmail,
    updateProfile,
    getAdditionalUserInfo,
    EmailAuthProvider,
    GoogleAuthProvider,
    OAuthProvider,
    User,
    UserCredential
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { auth, googleProvider, appleProvider, setLoggingOut } from '@shared/services/firebase';
import { updateUservars } from '@shared/utils/userVars';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { trackSignupConversion } from '@shared/services/conversionTracking';
import { recordAttributionVisit, recordInstallAttribution, associateAnonymousVisitor, linkAttributionToUser } from '@shared/services/attributionService';
import { recordLogin } from '@shared/services/activityDayService';
import { setSignupAppVersion, setLastSeenAppVersion } from '@shared/services/userContentService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@shared/services/firebase';
import { APP_VERSION } from '../version';
import { getNativeVersionInfo } from '@/lib/nativeVersion';
import InstallAttribution from '../plugins/InstallAttribution';
import { getAuthErrorMessage } from '../lib/authErrors';
import { clearLocalOnboardingCompletion } from '../hooks/useOnboardingRedirect';
import { usePresenceRecording } from '../hooks/usePresenceRecording';
import { logError, logDebug } from '@/lib/log';


export interface AuthContextType {
    currentUser: User | null;
    /** True if currentUser exists and is NOT anonymous (i.e. has a real account) */
    isAuthenticated: boolean;
    /** True if the current session created a new account (signup/link), false for login. Consumed once by AuthGuard. */
    isNewSignup: boolean;
    clearNewSignup: () => void;
    loading: boolean;
    signup: (email: string, password: string) => Promise<void>;
    login: (email: string, password: string) => Promise<void>;
    loginWithGoogle: () => Promise<void>;
    loginWithApple: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    logout: () => Promise<void>;
    error: string | null;
}

/** @internal Exported for demo mode — do not use directly in app components */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isNewSignup, setIsNewSignup] = useState(false);
    const lastKnownEmailRef = useRef<string | null>(null);
    // Native resume / midnight: AuthContext's onAuthStateChanged path does
    // not re-fire when iOS keeps the WebView alive, so returning-user
    // measurement would miss the day-2 open without this.
    usePresenceRecording(currentUser);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                // Create an anonymous account for every visitor so we can track
                // the full visitor → account funnel. Firebase Auth 30-day auto-
                // cleanup keeps anonymous user count manageable; the attribution
                // doc in Firestore survives cleanup for long-term analytics.
                //
                // Retried with backoff: onAuthStateChanged never fires again
                // after this handler, so a one-shot failure here used to strand
                // the app with no user FOREVER — an unrecoverable splash screen
                // (seen on-device 2026-07-21, relaunching after a crash mid
                // sign-out). If every attempt fails we still fall through so
                // `loading` resolves and AuthGuard can show its can't-connect
                // screen instead of the eternal splash.
                for (const delayMs of [0, 2_000, 8_000]) {
                    if (delayMs) await new Promise((r) => setTimeout(r, delayMs));
                    try {
                        await signInAnonymously(auth);
                        // onAuthStateChanged will fire again with the anonymous user
                        return;
                    } catch (err) {
                        logError(err, { context: 'anonymous_auth' });
                    }
                }
            }
            setCurrentUser(user);
            setLoading(false);
            // Record attribution visit for any authenticated user (anonymous or real).
            // Native resume / local-date rollover without remounting is covered by
            // usePresenceRecording — this path only runs on auth settle.
            if (user) {
                recordAttributionVisit(user.uid, user.isAnonymous);
                // Ensure an activityDays doc exists for today on every real login
                // and sync login event to HubSpot (platform, version, timestamp)
                if (!user.isAnonymous) {
                    recordLogin(user.uid).catch(() => {});
                    // Upsert the current build onto userContent so active users can be
                    // segmented by version (and existing users acquire lastSeenAppVersion
                    // on their next login). Cheap flat merge, no read on this hot path.
                    // Records the NATIVE version alongside the bundle version — the
                    // update-gate adoption tracker reads the native one, because an
                    // OTA can move the bundle version without moving the shell.
                    getNativeVersionInfo()
                        .then(({ platform, nativeVersion }) =>
                            setLastSeenAppVersion(user.uid, APP_VERSION, { platform, nativeVersion }))
                        .catch((err) => logError(err, { context: 'last_seen_app_version' }));
                    linkAttributionToUser(user.uid).catch(() => {});
                    httpsCallable(functions, 'enough_hubspot_sync_login')({
                        platform: Capacitor.getPlatform(),
                        appVersion: APP_VERSION,
                    }).catch((err) => logError(err, { context: 'hubspot_login_sync' }));

                    // Detect email change (after verifyBeforeUpdateEmail verification)
                    const prevEmail = lastKnownEmailRef.current;
                    if (prevEmail && user.email && prevEmail !== user.email) {
                        logDebug('[Auth] Email change detected, syncing to HubSpot');
                        httpsCallable(functions, 'enough_hubspot_sync_email_change')({
                            oldEmail: prevEmail,
                        }).catch((err) => logError(err, { context: 'hubspot_email_sync' }));
                    }
                    lastKnownEmailRef.current = user.email;
                }
                // On first native app launch, capture install attribution from platform APIs
                if (Capacitor.isNativePlatform() && !sessionStorage.getItem('install_attribution_checked')) {
                    sessionStorage.setItem('install_attribution_checked', 'true');
                    InstallAttribution.getData()
                        .then(data => recordInstallAttribution(user.uid, data))
                        // A thrown check is itself a measurable outcome — record it so a
                        // capture failure is distinguishable from a never-opened install.
                        .catch(() => recordInstallAttribution(user.uid, {
                            source: 'unknown',
                            attributed: false,
                            checkOutcome: 'error',
                        }));
                }
            }
        });

        return unsubscribe;
    }, []);

    const isAuthenticated = currentUser !== null && !currentUser.isAnonymous;

    const signup = async (email: string, password: string) => {
        setError(null);
        const previousAnonUid = currentUser?.isAnonymous ? currentUser.uid : null;
        try {
            if (currentUser?.isAnonymous) {
                // Link anonymous account → real account (preserves UID).
                // NOTE: linking mutates the same User object and does NOT fire
                // onAuthStateChanged. Data/Subscription contexts key their
                // effects on currentUser.isAnonymous and rely on the re-render
                // from setIsNewSignup(true) below to re-attach — keep that call.
                const credential = EmailAuthProvider.credential(email, password);
                await linkWithCredential(currentUser, credential);
                // linkWithCredential mutates the same User object and does NOT
                // fire onAuthStateChanged, so the non-anonymous side effects in
                // that handler never run for email signups. Replicate them inline,
                // exactly as promoteOrSignIn does for social signups:
                //   - recordLogin: writes the day-0 activityDays doc (without this,
                //     email signups were missing their first session from every
                //     activity/retention metric until their next app launch).
                //   - linkAttributionToUser: carries attribution onto the account.
                //   - HubSpot login sync: backfills e_signup_date (triggers the
                //     welcome email series) so signup props reach HubSpot
                //     immediately instead of waiting for the next launch / nightly
                //     full sync. (Google/Apple use signInWithCredential, which does
                //     fire onAuthStateChanged.)
                recordLogin(currentUser.uid).catch(() => {});
                linkAttributionToUser(currentUser.uid).catch(() => {});
                httpsCallable(functions, 'enough_hubspot_sync_login')({
                    platform: Capacitor.getPlatform(),
                    appVersion: APP_VERSION,
                }).catch((err) => logError(err, { context: 'hubspot_signup_link_sync' }));
            } else {
                await createUserWithEmailAndPassword(auth, email, password);
            }
            setIsNewSignup(true);
            // Awaited (bounded, never rejects): the conversion must reach the
            // native SDK before this resolves and the UI moves on, so a signup
            // that completes as the app is backgrounded isn't lost in the
            // WebView. link-in-place and create both leave auth.currentUser as
            // the new/linked account, so its uid is the stable transaction_id.
            await trackSignupConversion({ uid: auth.currentUser?.uid });
            // Record the immutable signup version (link-in-place and create both
            // leave auth.currentUser as the new/linked account). Write-once setter.
            if (auth.currentUser) {
                setSignupAppVersion(auth.currentUser.uid, APP_VERSION).catch((err) => logError(err, { context: 'signup_app_version' }));
            }
        } catch (err: any) {
            if (err?.code === 'auth/email-already-in-use' && previousAnonUid) {
                // Email exists — sign in normally (abandons anonymous UID)
                try {
                    const result = await signInWithEmailAndPassword(auth, email, password);
                    associateAnonymousVisitor(previousAnonUid, result.user.uid).catch(logError);
                    return;
                } catch {
                    // Fall through to show original error
                }
            }
            const message = getAuthErrorMessage(err);
            setError(message);
            logError(err, { tags: { auth_method: 'email_signup' } });
            throw err;
        }
    };

    const login = async (email: string, password: string) => {
        setError(null);
        const previousAnonUid = currentUser?.isAnonymous ? currentUser.uid : null;
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            if (previousAnonUid) {
                associateAnonymousVisitor(previousAnonUid, result.user.uid).catch(logError);
            }
        } catch (err: any) {
            const message = getAuthErrorMessage(err);
            setError(message);
            logError(err, { tags: { auth_method: 'email_login' } });
            throw err;
        }
    };

    /**
     * Promote the in-session anonymous user to a real account WITHOUT switching
     * uids when possible. For a brand-new social user, linking the credential to
     * the anonymous user preserves the uid, so every pre-auth doc (attribution,
     * experiment assignment, reflection, onboarding) carries over with zero
     * reconciliation — the same happy path email signup already uses.
     *
     * Only a genuinely returning account (the credential is already in use) forces
     * a uid switch; there we sign in normally and stitch the abandoned anonymous
     * session via associateAnonymousVisitor (the server reconciler then carries its
     * data forward). This is the source-side half of the identity-stitching fix:
     * the majority (new social signups) stop minting orphaned uids entirely.
     *
     * Returns whether this was a new signup (link succeeded, or sign-in reports a
     * new user). `link` mutates the same User object and does NOT fire
     * onAuthStateChanged, so we replicate the non-anonymous login side effects
     * (HubSpot login sync — which backfills e_signup_date — plus recordLogin and
     * linkAttributionToUser) inline, exactly as the email signup path does.
     */
    const promoteOrSignIn = async (
        link: () => Promise<UserCredential>,
        signInAfterLinkFailure: (linkErr: unknown) => Promise<UserCredential>,
        previousAnonUid: string | null,
    ): Promise<boolean> => {
        const stitchAndReport = (result: UserCredential): boolean => {
            if (previousAnonUid && auth.currentUser && previousAnonUid !== auth.currentUser.uid) {
                associateAnonymousVisitor(previousAnonUid, auth.currentUser.uid).catch(logError);
            }
            return getAdditionalUserInfo(result)?.isNewUser ?? false;
        };

        if (previousAnonUid) {
            try {
                const linked = await link();
                recordLogin(linked.user.uid).catch(() => {});
                linkAttributionToUser(linked.user.uid).catch(() => {});
                httpsCallable(functions, 'enough_hubspot_sync_login')({
                    platform: Capacitor.getPlatform(),
                    appVersion: APP_VERSION,
                }).catch((err) => logError(err, { context: 'hubspot_signup_link_sync' }));
                return true;
            } catch (err: any) {
                const code = err?.code ?? '';
                // Returning user (the social account already exists) — only these
                // genuinely require a uid switch. Anything else is a real failure.
                const credentialAlreadyExists =
                    code === 'auth/credential-already-in-use' ||
                    code === 'auth/email-already-in-use' ||
                    code === 'auth/account-exists-with-different-credential';
                if (!credentialAlreadyExists) throw err;
                return stitchAndReport(await signInAfterLinkFailure(err));
            }
        }
        return stitchAndReport(await signInAfterLinkFailure(null));
    };

    const loginWithGoogle = async () => {
        setError(null);
        const previousAnonUid = currentUser?.isAnonymous ? currentUser.uid : null;
        const anonUser = auth.currentUser;
        try {
            let isNewUser = false;
            if (Capacitor.isNativePlatform()) {
                const result = await FirebaseAuthentication.signInWithGoogle({
                    skipNativeAuth: true,
                });
                const credential = GoogleAuthProvider.credential(
                    result.credential?.idToken,
                    result.credential?.accessToken
                );
                isNewUser = await promoteOrSignIn(
                    () => linkWithCredential(anonUser!, credential),
                    () => signInWithCredential(auth, credential),
                    previousAnonUid,
                );
            } else {
                isNewUser = await promoteOrSignIn(
                    () => linkWithPopup(anonUser!, googleProvider),
                    // Reuse the credential from the failed link so a returning web
                    // user isn't prompted with a second popup.
                    (linkErr) => {
                        const cred = linkErr instanceof FirebaseError ? GoogleAuthProvider.credentialFromError(linkErr) : null;
                        return cred ? signInWithCredential(auth, cred) : signInWithPopup(auth, googleProvider);
                    },
                    previousAnonUid,
                );
            }
            if (isNewUser) {
                setIsNewSignup(true);
                // Bounded await — see the email signup path for why.
                await trackSignupConversion({ uid: auth.currentUser?.uid });
                // Record the immutable signup version for the new social account.
                if (auth.currentUser) {
                    setSignupAppVersion(auth.currentUser.uid, APP_VERSION).catch((err) => logError(err, { context: 'signup_app_version' }));
                }
            }
        } catch (err: any) {
            // Diagnostics belong in Sentry (above), never in the user's face:
            // the native branch used to render `Google sign-in failed [code]:
            // <raw message or JSON.stringify(err)>`, which showed a stack-ish
            // dump to the user — and turned a tapped "Cancel" into what looked
            // like a crash. `logError` already carries the code, message and
            // platform tags, so nothing is lost by wording this kindly.
            logError(err, { context: 'google_sign_in', tags: { auth_method: 'google', platform: Capacitor.getPlatform() } });
            setError(getAuthErrorMessage(err));
            throw err;
        }
    };

    const loginWithApple = async () => {
        setError(null);
        const previousAnonUid = currentUser?.isAnonymous ? currentUser.uid : null;
        const anonUser = auth.currentUser;
        try {
            let isNewUser = false;
            // Apple returns the user's name ONLY on the first authorization for this
            // app — independent of Private Relay / hidden email — and never again.
            // Capture it now or we lose it: native surfaces it as result.user.displayName
            // (the plugin builds "Given Family" from the Apple fullName scope), web
            // populates currentUser.displayName on first sign-in.
            let appleDisplayName: string | null = null;
            if (Capacitor.isNativePlatform()) {
                const result = await FirebaseAuthentication.signInWithApple({
                    skipNativeAuth: true,
                });
                appleDisplayName = result.user?.displayName ?? null;
                const provider = new OAuthProvider('apple.com');
                const credential = provider.credential({
                    idToken: result.credential?.idToken,
                    rawNonce: result.credential?.nonce,
                });
                // Native Apple CANNOT use promoteOrSignIn's link-first pattern. Apple's
                // OIDC id_token is single-use at Firebase's backend: linkWithCredential
                // spends it, and for a returning user the link fails with
                // credential-already-in-use — but unlike Google, Apple does NOT hand back
                // a reusable pendingToken-backed credential via credentialFromError, so the
                // sign-in fallback re-submits the spent token → auth/missing-or-invalid-nonce
                // ("Duplicate credential received"). Link-in-place uid preservation is
                // therefore impossible here. Sign in once (single token exchange, the proven
                // pre-identity-stitching behavior) and stitch the anonymous session via the
                // server reconciler — the same returning-user path promoteOrSignIn falls back
                // to. (Google/web keep link-first; their tokens are reusable.)
                const userCredential = await signInWithCredential(auth, credential);
                isNewUser = getAdditionalUserInfo(userCredential)?.isNewUser ?? false;
                if (previousAnonUid && auth.currentUser && previousAnonUid !== auth.currentUser.uid) {
                    associateAnonymousVisitor(previousAnonUid, auth.currentUser.uid).catch(logError);
                }
            } else {
                isNewUser = await promoteOrSignIn(
                    () => linkWithPopup(anonUser!, appleProvider),
                    (linkErr) => {
                        const cred = linkErr instanceof FirebaseError ? OAuthProvider.credentialFromError(linkErr) : null;
                        return cred ? signInWithCredential(auth, cred) : signInWithPopup(auth, appleProvider);
                    },
                    previousAnonUid,
                );
                appleDisplayName = auth.currentUser?.displayName ?? null;
            }
            // Persist the Apple name app-wide (Firebase displayName + uservars first/last).
            // Presence implies first auth, so this won't clobber a name the user edits later.
            const appleUser = auth.currentUser;
            if (appleUser && appleDisplayName && appleDisplayName.trim()) {
                const [firstName, ...rest] = appleDisplayName.trim().split(/\s+/);
                const lastName = rest.join(' ');
                if (!appleUser.displayName) {
                    updateProfile(appleUser, { displayName: appleDisplayName.trim() })
                        .catch((err) => logError(err, { context: 'apple_update_profile' }));
                }
                updateUservars(appleUser.uid, { firstName, ...(lastName ? { lastName } : {}) })
                    .catch((err) => logError(err, { context: 'apple_name_uservars' }));
            }
            if (isNewUser) {
                setIsNewSignup(true);
                // Bounded await — see the email signup path for why.
                await trackSignupConversion({ uid: auth.currentUser?.uid });
                // Record the immutable signup version for the new social account.
                if (auth.currentUser) {
                    setSignupAppVersion(auth.currentUser.uid, APP_VERSION).catch((err) => logError(err, { context: 'signup_app_version' }));
                }
            }
        } catch (err: any) {
            // See the note in loginWithGoogle — same raw-diagnostic leak, same fix.
            logError(err, { context: 'apple_sign_in', tags: { auth_method: 'apple', platform: Capacitor.getPlatform() } });
            setError(getAuthErrorMessage(err));
            throw err;
        }
    };

    const resetPassword = async (email: string) => {
        setError(null);
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (err: any) {
            const message = getAuthErrorMessage(err);
            setError(message);
            logError(err, { tags: { auth_method: 'reset_password' } });
            throw err;
        }
    };

    const logout = async () => {
        setError(null);
        try {
            setLoggingOut(true);
            // Reset onboarding flag so the next user gets the full flow
            clearLocalOnboardingCompletion();
            await signOut(auth);
            // signOut triggers onAuthStateChanged → null user → signInAnonymously
            // Clear route so user doesn't return to profile/settings on next login
            window.history.replaceState(null, '', '/');
        } catch (err: any) {
            const message = getAuthErrorMessage(err);
            setError(message);
            throw err;
        } finally {
            // Clear after a short delay to cover all in-flight snapshot errors
            setTimeout(() => setLoggingOut(false), 2000);
        }
    };

    const clearNewSignup = () => setIsNewSignup(false);

    const value = {
        currentUser,
        isAuthenticated,
        isNewSignup,
        clearNewSignup,
        loading,
        signup,
        login,
        loginWithGoogle,
        loginWithApple,
        resetPassword,
        logout,
        error
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
